import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Check, Plus, ArrowLeft, Loader2, ShoppingCart, User2, FileDown } from "lucide-react";

export default function NoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, inCart } = useCart();
  const [note, setNote] = useState(null);
  const [owned, setOwned] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/notes/${id}`).then((r) => setNote(r.data)).finally(() => setLoading(false));
    if (localStorage.getItem("nb_token")) {
      api.get("/purchases/ids").then((r) => setOwned(r.data.includes(id))).catch(() => {});
    }
  }, [id]);

  if (loading)
    return (
      <div className="flex justify-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  if (!note)
    return <div className="text-center py-32 text-muted-foreground">Note not found.</div>;

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-10">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-8 transition-colors"
        data-testid="back-button"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="grid md:grid-cols-2 gap-10">
        <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-border">
          {note.has_cover ? (
            <img src={`${API}/notes/${note.id}/cover`} alt={note.title} className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <FileText className="h-20 w-20 text-white/80" />
            </div>
          )}
        </div>

        <div>
          <Badge variant="secondary" className="uppercase tracking-overline text-[10px]">
            {note.subject}
          </Badge>
          <h1 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight text-slate-900 mt-4 leading-tight">
            {note.title}
          </h1>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-3">
            <User2 className="h-4 w-4" /> Sold by {note.seller_name}
            {note.pages ? <span>· {note.pages} pages</span> : null}
          </div>

          <p className="text-base text-slate-700 leading-relaxed mt-6 whitespace-pre-line">
            {note.description}
          </p>

          <div className="mt-8 p-6 rounded-2xl border border-border bg-slate-50">
            <div className="flex items-end justify-between">
              <div>
                <p className="text-xs uppercase tracking-overline text-muted-foreground">Price</p>
                <p className="font-heading font-extrabold text-4xl text-slate-900" data-testid="detail-price">
                  ₹{note.price}
                </p>
              </div>
              {owned ? (
                <Button onClick={() => navigate("/purchases")} className="rounded-full gap-2" data-testid="go-to-library">
                  <FileDown className="h-4 w-4" /> Download
                </Button>
              ) : (
                <div className="flex gap-3">
                  <Button
                    variant={inCart(note.id) ? "secondary" : "outline"}
                    className="rounded-full gap-2"
                    onClick={() => addItem(note)}
                    disabled={inCart(note.id)}
                    data-testid="detail-add-cart"
                  >
                    {inCart(note.id) ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                    {inCart(note.id) ? "In cart" : "Add to cart"}
                  </Button>
                  <Button
                    className="rounded-full gap-2"
                    onClick={() => {
                      if (!inCart(note.id)) addItem(note);
                      navigate("/cart");
                    }}
                    data-testid="detail-buy-now"
                  >
                    <ShoppingCart className="h-4 w-4" /> Buy now
                  </Button>
                </div>
              )}
            </div>
          </div>
          {owned && (
            <p className="mt-4 text-sm text-emerald-600 font-medium flex items-center gap-1">
              <Check className="h-4 w-4" /> You own this — find it in{" "}
              <Link to="/purchases" className="underline">My Notes</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
